export const caseBySlug = /* GraphQL */ `
  query CustomCaseBySlug($slug: String!) {
    caseBySlug(slug: $slug) {
      items {
        id
      }
    }
  }
`;

export const listCaseMilestones = /* GraphQL */ `
  query ListCaseMilestones($id: ID!) {
    getCase(id: $id) {
      completedMilestones {
        completedByID
        completionDate
        milestone
        receiptNumber
      }
    }
  }
`;

export const listCaseFiles = /* GraphQL */ `
  query ListCaseFiles($id: ID!, $nextToken: String) {
    getCase(id: $id) {
      files(nextToken: $nextToken) {
        items {
          id
          fileID
        }
        nextToken
      }
    }
  }
`;

/**
 * Drains the remaining pages of a case's files connection.
 *
 * 'getCase' selects 'files' without a limit, so AppSync caps it at 100 items
 * (see Case.files.req.vtl) and the 'byCase' index is sorted by 'fileID' — a
 * random UUID — so the 100 that come back are an arbitrary subset. Cases with
 * more than 100 documents silently lost the rest, including files that had
 * just been uploaded. Selects the same fields as 'getCase'.files so the pages
 * merge cleanly.
 */
export const listCaseFilesPage = /* GraphQL */ `
  query ListCaseFilesPage($id: ID!, $nextToken: String) {
    getCase(id: $id) {
      files(nextToken: $nextToken) {
        items {
          __typename
          id
          file {
            __typename
            id
            expirationDate
            durationOfStatus
            issueDate
            dueDate
            fileName
            fileType
            fileRequirement
            fileCategory
            contentType
            isFlattened
            original {
              bucket
              region
              key
              level
              identityId
            }
            processRequestID
            companyID
            sourceID
            ownerNationalID
            uploadDate
            uploaderID
            uploaderEmployer {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            uploaderForeignNational {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            uploaderTeamMember {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            createdAt
            updatedAt
          }
        }
        nextToken
      }
    }
  }
`;

export const listCaseFileRequirements = /* GraphQL */ `
  query ListCaseFileRequirements($id: ID!) {
    getCase(id: $id) {
      fileRequirements {
        type
        assignedToID
        expirationDateRequired
        issueDateRequired
        isApproved
        isRequested
        isAssignedToLegalAssistants
        durationOfStatus
      }
    }
  }
`;

export const getRecentlyViewedCase = /* GraphQL */ `
  query GetRecentlyViewedCase($id: ID!) {
    getCase(id: $id) {
      id
      slug
      currentPhase
      caseNumber
      requestedProcess
      startDate
      state
      caseManagerID
      caseManager {
        id
        firstname
        lastname
        slug
      }
      companyID
      company {
        id
        name
        displayCompanyName
      }
      foreignNationalID
      foreignNational {
        id
        firstname
        lastname
        slug
        immigrationStatus {
          expiration
        }
      }
      phaseRequirements {
        phase
        milestones
      }
      completedMilestones {
        completionDate
        completedByID
        milestone
        receiptNumber
      }
    }
  }
`;

/* Keep these fields synced to the custom updateCase below */
export const getCase = /* GraphQL */ `
  query CustomGetCase($id: ID!, $messagesUserGroup: UserGroup, $filesNextToken: String) {
    getCase(id: $id) {
      ### Start fields ###
      id
      slug
      currentPhase
      caseNumber
      attorneyID
      caseManagerID
      contributorIds
      companyID
      sponsoringEmployerID
      sponsoringNationalID
      foreignNationalID
      prevailingWageCaseNumber
      USCISReceiptNumber
      I485JReceiptNotice
      SelectionNoticeNo
      requestedProcessAdditionalProcessing
      AOSReceiptNotice
      individualSponsored
      ETACaseNumber
      USCISReceiptDetails {
        items {
          id
          caseID
          receiptNumber
          receiptType
          formType
          submittedDate
          modifiedDate
          currentCaseStatusText
          currentCaseStatusDesc
          histCaseStatus {
            date
            completedText
          }
          message
          isActive
          isLatest
          createdAt
          updatedAt
        }
      }
      complianceList {
        id
        caseID
        educationEvaluationNeeded
        stemExtEligible
        capGap
        wageIssues
      }
      PERMFilingData {
        id
        caseID
        PERMFirstPossibleFilingDate
        PERMLastPossibleFilingDate
        applicantsReceived
      }
      additionalFields {
        id
        caseID
        createdAt
        fields
      }
      source {
        id
        individualSponsored
        vendor {
          id
          name
          type
          location
          email
        }
      }
      shipmentTracking {
        id
        item
        date
        trackingNumber
        provider
      }
      isShipmentTrackingElectronically
      welcomeLetterAcknowledged
      welcomeLetterAcknowledgementDate
      welcomeLetterAcknowledgedBy
      relativeAdmins
      startDate
      closeDate
      closeReason
      completeDate
      employmentData {
        location {
          remoteAddress {
            address
            unitType
          }
          type
        }
        notes
        premiumProcessing
        startDate
        salary {
          currency
          amount
        }
      }
      jobDescriptionID
      jobDescription {
        canWorkFromHome
        certificationLicenses
        companyID
        degrees
        dotNumber
        caseSocCode
        duties
        id
        jobURL
        majors
        objectiveSkillSetsRequired
        offeredSalary
        officialTitle
        requiresBachelors
        requiresMasters
        requiresPhd
        supervisesOthers
        supervisesOthersCount
        supervisesTitles
        supervisorTitle
        title
        travelDomestic
        travelInternational
        travelIsRequired
        travelPercentage
        type
        uploadedDescriptionID
        yearsGeneralExperience
        uploadedDescription {
          id
          expirationDate
          fileName
          fileType
          contentType
          isFlattened
          sourceID
          ownerNationalID
          uploadDate
          original {
            bucket
            identityId
            key
            level
            region
          }
        }
        specialRequirements
        createdAt
        updatedAt
      }
      resumeID
      resume {
        fileName
        contentType
        isFlattened
        createdAt
        original {
          bucket
          key
          level
          region
          identityId
        }
      }
      files(nextToken: $filesNextToken) {
        items {
          __typename
          id
          file {
            __typename
            id
            expirationDate
            durationOfStatus
            issueDate
            dueDate
            fileName
            fileType
            fileRequirement
            fileCategory
            contentType
            isFlattened
            original {
              bucket
              region
              key
              level
              identityId
            }
            processRequestID
            companyID
            sourceID
            ownerNationalID
            uploadDate
            uploaderID
            uploaderEmployer {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            uploaderForeignNational {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            uploaderTeamMember {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            createdAt
            updatedAt
          }
        }
        nextToken
      }
      fileRequirements {
        type
        fileType
        fileCategory
        fileInstruction
        defaultStorage
        visibility
        assignedToID
        expirationDateRequired
        issueDateRequired
        isApproved
        isRequested
        isAssignedToLegalAssistants
        durationOfStatus
      }
      phaseRequirements {
        phase
        milestones
      }
      completedMilestones {
        completionDate
        completedByID
        milestone
        receiptNumber
      }
      preparedForms {
        items {
          id
          caseID
          activities {
            items {
              id
              activityType
              actorID
              actorEmployer {
                id
                slug
                firstname
                lastname
              }
              actorForeignNational {
                id
                slug
                firstname
                lastname
              }
              actorTeamMember {
                id
                slug
                firstname
                lastname
              }
            }
          }
          requiresAttorneyApproval
          attorneyApprover {
            firstname
            lastname
            slug
            id
            username
          }
          attorneyApprovalDate
          attorneyApproverID

          requiresSponsorApproval
          sponsorApprovalDate
          sponsorApproverEmployer {
            firstname
            lastname
            slug
            id
            username
          }
          sponsorApproverNational {
            firstname
            lastname
            slug
            id
            username
          }
          sponsorApproverID

          requiresNationalApproval
          nationalApprover {
            firstname
            lastname
            slug
            id
            username
          }
          nationalApprovalDate
          nationalApproverID

          finalCaseManagerApprover {
            firstname
            lastname
            slug
            id
          }
          finalCaseManagerApprovalDate
          finalCaseManagerApproverID

          employersCanView
          nationalsCanView

          caseID
          companyID
          completedFormID
          createdAt
          id
          foreignNationalID
          preparerID
          relativeAdmins
          state
          templateID
          case {
            caseManagerID
          }
          preparer {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
            }
          }
          completedForm {
            id
            expirationDate
            fileName
            fileType
            contentType
            isFlattened
            original {
              bucket
              region
              key
              level
              identityId
            }
            processRequestID
            companyID
            sourceID
            ownerNationalID
            uploadDate
            uploaderID
            uploaderEmployer {
              id
              firstname
              lastname
              slug
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
              }
            }
            uploaderForeignNational {
              id
              firstname
              lastname
              slug
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            uploaderTeamMember {
              id
              firstname
              lastname
              slug
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            createdAt
            updatedAt
          }
          assignedToID
          assignedToEmployer {
            id
            firstname
            lastname
            slug
          }
          assignedToTeamMember {
            id
            firstname
            lastname
            slug
          }
          assignedToNational {
            id
            firstname
            lastname
            slug
          }

          rejectedByID
          rejectedDate
          rejectedByEmployer {
            id
            firstname
            lastname
            slug
          }
          rejectedByTeamMember {
            id
            firstname
            lastname
            slug
          }
          rejectedByNational {
            id
            firstname
            lastname
            slug
          }
          completedFormHistory {
            items {
              original {
                bucket
                identityId
                key
                level
                region
              }
              fileName
              fileType
              id
              uploadDate
            }
          }
          comments {
            items {
              id
            }
          }
        }
      }
      requestedProcess
      requestedProcessSubCategory
      requestedProcessAdditionalProcessing
      globalRequestedProcessSubCategory
      globalProcessCountryCode
      globalProcessDuration
      globalVisaType
      vendorID
      sourceID
      state
      company {
        id
        name
        displayCompanyName
        lowerNameKey
        slug
        archived
        assignedEmployers {
          items {
            employer {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
              }
            }
          }
        }
        data {
          I9_audit_has_been_completed_in_past_18_months
          current_number_of_H1B_employees
          current_number_of_employees_on_L_visas
          other_visa_statuses_sponsored
          email_address_invoices_should_be_addressed_to
          name_invoices_should_be_addressed_to
          shippingPreference {
            fedexAccount
            preference
            upsAccount
          }
          yearCompanyWasFoundedInUS
          utilizePremiumProcessingService
          usesWageSurvey
          usesHRSSystem {
            reason
            value
          }
          usesEVerify
          usesApplicantTrackingSystem {
            reason
            value
          }
          updatedAt
          staffsEmployeesAtThirdPartyEndClientSites
          shipOriginalApprovalNotices
          preferredShippingSpeedForDocuments
          placesEmployeesAtClientSitesForMoreThanACoupleOfDaysAtATime
          phone
          permissionReleasePetitionsRFE
          permissionReleaseLCA
          permissionReleaseApprovalNotices
          permissionRelease9089
          permissionPowerAttorney
          paymentPreference
          partTimeEmployeesInUS
          netRevenueForMostRecentFiscalYear {
            amount
            currency
          }
          naicsCodeOfCompany
          maintainsAWrittenImmigrationPolicy
          id
          grossRevenueForMostRecentFiscalYear {
            amount
            currency
          }
          fullTimeEmployeesInUS
          fein
          fax
          employeesEmailMHAttorneysAndParalegalsDirectly
          educationEvaluation
          doingBusinessAs
          createdAt
          companyCoversExpenseOfDependentPetitionsAndOrEADCards
          companyCoversExpenseOfDependentPetitions
          requiresMeetingReminder
          annualMeetingDate
          placesEmployeesAtClientSitesForMoreThan30DaysAtATime
          isH1BDependentEmloyer
          hasBeenWillfulViolator
          isHigherEd_20_USC_1001_a
          isHigherEdNonProfit_8_CFR_214_2_h_19_iii_B
          isHigherEdNonProfit_8_CFR_214_2_h_8_ii_F_2
          isNonProfitResearch_8_CFR_214_2_h_19_iii_C
          isNonProfitResearch_8_CFR_214_2_h_8_ii_F_3
          isQualifyingCapExempt_8_CFR_214_2_h_8_ii_F_4
          technologyReleaseLicenseRequirement
          employs50OrMore {
            value
            followPrompt
            followValue
          }
          allowsEmployeesToTelecommute {
            value
            followPrompt
            followValue
            followPromptSecond
            followValueSecond
          }
          preferenceForEmployeesToPayPartOfGreenCard {
            value
            reason
          }
          preferenceForEmployeesToPayPartOfH1B {
            value
            reason
          }
        }
      }
      sponsoringNational {
        id
        firstname
        lastname
        slug
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
      }
      foreignNational {
        type
        id
        slug
        email
        emailPersonal
        emailWork
        employeeId
        username
        firstname
        middlename
        lastname
        nickname
        additionalFirstnames
        additionalMiddlenames
        additionalLastnames
        lowerFirst
        lowerLast
        lowerFull
        maritalStatus
        spouse
        dependents
        phone
        phoneWork
        companyID
        mostRecentlyInvited
        archived
        relatives
        relativeAdmins
        nonImmigrantVisaNumber
        visaIssueDate
        visaExpiration
        visaConsulate
        immigrantVisaApplicant
        immigrantPetitionEverFiled
        hasPendingETA9089
        hasValidOrExpiredEAD
        hasPendingUSCISApplication
        hasHadVisaPetitionRevokedOrDenied
        hasBeenArrestedSinceLastUSEntry
        hasTransportedWeapons
        hasParticipatedInArmedGroup
        hasDetailedPerson
        hasUsedWeapons
        hasWeaponsTraining
        hasCriminalOffence
        hasViolatedVisaTerms
        citizenshipCountry
        birthCountry
        birthState
        birthCity
        birthDate
        jobDescriptionID
        isRemote
        hasNonUSPassport
        passportGender
        passportIssueDate
        passportExpirationDate
        passportNumber
        passportCountry
        usSSN
        aNumber
        lastEntryUS
        i94ArrivalDepartureRecordNumber
        sevisNumber
        eadNumber
        createdAt
        updatedAt
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
        immigrationStatus {
          status
          expiration
        }
        cases {
          items {
            id
            slug
            state
            startDate
            requestedProcess
            requestedProcessSubCategory
            requestedProcessAdditionalProcessing
            globalRequestedProcessSubCategory
            globalProcessCountryCode
            source {
              requestedProcess
            }
          }
        }
        relativesAsSource {
          items {
            id
            type
            target {
              firstname
              id
              lastname
              slug
              birthDate
              immigrationStatus {
                status
                expiration
              }
              avatar {
                original {
                  bucket
                  identityId
                  key
                  level
                  region
                }
                squareThumbnails {
                  size
                  image {
                    bucket
                    identityId
                    key
                    level
                    region
                  }
                }
              }
              cases {
                items {
                  id
                  slug
                  state
                  startDate
                  requestedProcess
                  requestedProcessSubCategory
                  requestedProcessAdditionalProcessing
                  globalRequestedProcessSubCategory
                  globalProcessCountryCode
                  source {
                    requestedProcess
                  }
                }
              }
            }
          }
        }
        relativesAsTarget {
          items {
            id
            type
            source {
              firstname
              id
              lastname
              slug
              birthDate
              immigrationStatus {
                status
                expiration
              }
              avatar {
                original {
                  bucket
                  identityId
                  key
                  level
                  region
                }
                squareThumbnails {
                  size
                  image {
                    bucket
                    identityId
                    key
                    level
                    region
                  }
                }
              }
              cases {
                items {
                  id
                  slug
                  state
                  startDate
                  requestedProcess
                  requestedProcessSubCategory
                  requestedProcessAdditionalProcessing
                  globalRequestedProcessSubCategory
                  globalProcessCountryCode
                  source {
                    requestedProcess
                  }
                }
              }
            }
          }
        }
      }
      sponsoringEmployer {
        id
        firstname
        lastname
        slug
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
      }
      attorney {
        id
        firstname
        lastname
        slug
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
      }
      caseManager {
        id
        email
        firstname
        lastname
        slug
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
      }
      caseNotes(
        userGroupPostDate: { beginsWith: { userGroup: $messagesUserGroup } }
      ) {
        items {
          authorID
          authorEmployerID
          authorEmployer {
            id
            lastname
            firstname
            avatar {
              original {
                bucket
                identityId
                key
                level
                region
              }
              squareThumbnails {
                image {
                  region
                  level
                  key
                  identityId
                  bucket
                }
                size
              }
            }
          }
          authorTeamMember {
            id
            lastname
            firstname
            avatar {
              original {
                bucket
                identityId
                key
                level
                region
              }
              squareThumbnails {
                image {
                  region
                  level
                  key
                  identityId
                  bucket
                }
                size
              }
            }
          }
          id
          createdAt
          updatedAt
          note
        }
      }
      contributors {
        items {
          id
          contributorID
          contributorEmployer {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          contributorForeignNational {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          contributorTeamMember {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
        }
      }
      tasks {
        items {
          action
          category
          deadline
          deadlineIsManuallySet
          description
          duration
          groupsCanRead
          isCustom
          isKeyDate
          keyDateAlternateAction
          isPaymentStatus
          paymentStatusAlternateAction
          orderIndex
          assignedToID
          orderIndex
          isAssignedToLegalAssistants
          assignedToTeamMember {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          assignedToForeignNational {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          assignedToEmployer {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          caseID
          case {
            attorneyID
            caseManagerID
          }
          caseState
          state
          startDate
          completeDate
          id
          prerequisiteID
          prerequisite {
            action
            category
            orderIndex
          }
          dependencies {
            items {
              id
              state
              deadline
              duration
              templateID
              startDate
              prerequisiteID
              action
              completeDate
            }
          }
        }
      }
      ### End fields ###
    }
  }
`;

export const updateCase = /* GraphQL */ `
  mutation CustomUpdateCase(
    $input: UpdateCaseInput!
    $messagesUserGroup: UserGroup!
  ) {
    updateCase(input: $input) {
      ### Start fields ###
      id
      slug
      currentPhase
      caseNumber
      attorneyID
      caseManagerID
      companyID
      sponsoringEmployerID
      sponsoringNationalID
      individualSponsored
      foreignNationalID
      prevailingWageCaseNumber
      USCISReceiptNumber
      SelectionNoticeNo
      requestedProcessAdditionalProcessing
      AOSReceiptNotice
      I485JReceiptNotice
      ETACaseNumber
      complianceListID
      PERMFilingDataID
      complianceList {
        id
        caseID
        educationEvaluationNeeded
        stemExtEligible
        capGap
        wageIssues
      }
      PERMFilingData {
        id
        caseID
        PERMFirstPossibleFilingDate
        PERMLastPossibleFilingDate
        applicantsReceived
      }
      additionalFields {
        id
        caseID
        createdAt
        fields
      }
      shipmentTracking {
        id
        item
        date
        trackingNumber
        provider
      }
      isShipmentTrackingElectronically
      welcomeLetterAcknowledged
      welcomeLetterAcknowledgementDate
      welcomeLetterAcknowledgedBy
      relativeAdmins
      startDate
      closeDate
      closeReason
      completeDate
      employmentData {
        location {
          remoteAddress {
            address
            unitType
          }
          type
        }
        notes
        premiumProcessing
        startDate
        salary {
          currency
          amount
        }
      }
      jobDescriptionID
      jobDescription {
        canWorkFromHome
        certificationLicenses
        companyID
        degrees
        dotNumber
        duties
        id
        jobURL
        majors
        objectiveSkillSetsRequired
        offeredSalary
        officialTitle
        requiresBachelors
        requiresMasters
        requiresPhd
        supervisesOthers
        supervisesOthersCount
        supervisesTitles
        supervisorTitle
        title
        travelDomestic
        travelInternational
        travelIsRequired
        travelPercentage
        type
        uploadedDescriptionID
        yearsGeneralExperience
        uploadedDescription {
          id
          expirationDate
          fileName
          fileType
          contentType
          isFlattened
          sourceID
          ownerNationalID
          uploadDate
          original {
            bucket
            identityId
            key
            level
            region
          }
        }
        specialRequirements
        createdAt
        updatedAt
      }
      resumeID
      resume {
        fileName
        contentType
        isFlattened
        createdAt
        original {
          bucket
          key
          level
          region
          identityId
        }
      }
      files {
        items {
          __typename
          id
          file {
            __typename
            id
            expirationDate
            durationOfStatus
            issueDate
            dueDate
            fileName
            fileType
            fileRequirement
            fileCategory
            contentType
            isFlattened
            original {
              bucket
              region
              key
              level
              identityId
            }
            processRequestID
            companyID
            sourceID
            ownerNationalID
            uploadDate
            uploaderID
            uploaderEmployer {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            uploaderForeignNational {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            uploaderTeamMember {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            createdAt
            updatedAt
          }
        }
      }
      fileRequirements {
        type
        fileType
        fileCategory
        fileInstruction
        defaultStorage
        visibility
        assignedToID
        expirationDateRequired
        issueDateRequired
        isApproved
        isRequested
        isAssignedToLegalAssistants
        durationOfStatus
      }
      phaseRequirements {
        phase
        milestones
      }
      completedMilestones {
        completionDate
        completedByID
        milestone
        receiptNumber
      }
      preparedForms {
        items {
          id
          caseID
          activities {
            items {
              id
              activityType
              actorID
              actorEmployer {
                id
                slug
                firstname
                lastname
              }
              actorForeignNational {
                id
                slug
                firstname
                lastname
              }
              actorTeamMember {
                id
                slug
                firstname
                lastname
              }
            }
          }
          requiresAttorneyApproval
          attorneyApprover {
            firstname
            lastname
            slug
            id
            username
          }
          attorneyApprovalDate
          attorneyApproverID
          requiresSponsorApproval
          sponsorApprovalDate
          sponsorApproverEmployer {
            firstname
            lastname
            slug
            id
            username
          }
          sponsorApproverNational {
            firstname
            lastname
            slug
            id
            username
          }
          sponsorApproverID

          requiresNationalApproval
          nationalApprover {
            firstname
            lastname
            slug
            id
            username
          }
          nationalApprovalDate
          nationalApproverID

          finalCaseManagerApprover {
            firstname
            lastname
            slug
            id
          }
          finalCaseManagerApprovalDate
          finalCaseManagerApproverID

          employersCanView
          nationalsCanView

          caseID
          companyID
          completedFormID
          createdAt
          id
          foreignNationalID
          preparerID
          relativeAdmins
          state
          templateID
          case {
            caseManagerID
          }
          preparer {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
            }
          }
          completedForm {
            id
            expirationDate
            fileName
            fileType
            contentType
            isFlattened
            original {
              bucket
              region
              key
              level
              identityId
            }
            processRequestID
            companyID
            sourceID
            ownerNationalID
            uploadDate
            uploaderID
            uploaderEmployer {
              id
              firstname
              lastname
              slug
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
              }
            }
            uploaderForeignNational {
              id
              firstname
              lastname
              slug
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            uploaderTeamMember {
              id
              firstname
              lastname
              slug
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
                version
              }
            }
            createdAt
            updatedAt
          }
          assignedToID
          assignedToEmployer {
            id
            firstname
            lastname
            slug
          }
          assignedToTeamMember {
            id
            firstname
            lastname
            slug
          }
          assignedToNational {
            id
            firstname
            lastname
            slug
          }
          rejectedByID
          rejectedDate
          rejectedByEmployer {
            id
            firstname
            lastname
            slug
          }
          rejectedByTeamMember {
            id
            firstname
            lastname
            slug
          }
          rejectedByNational {
            id
            firstname
            lastname
            slug
          }
          completedFormHistory {
            items {
              original {
                bucket
                identityId
                key
                level
                region
              }
              fileName
              fileType
              id
              uploadDate
            }
          }
          comments {
            items {
              id
            }
          }
        }
      }
      requestedProcess
      requestedProcessSubCategory
      requestedProcessAdditionalProcessing
      globalRequestedProcessSubCategory
      globalProcessCountryCode
      globalProcessDuration
      globalVisaType
      vendorID
      sourceID
      state
      company {
        id
        name
        displayCompanyName
        lowerNameKey
        slug
        archived
        assignedEmployers {
          items {
            employer {
              id
              firstname
              lastname
              lowerFull
              slug
              type
              avatar {
                original {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                squareThumbnails {
                  image {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  size
                }
              }
            }
          }
        }
        data {
          shippingPreference {
            fedexAccount
            preference
            upsAccount
          }
          yearCompanyWasFoundedInUS
          utilizePremiumProcessingService
          usesWageSurvey
          usesHRSSystem {
            reason
            value
          }
          usesEVerify
          usesApplicantTrackingSystem {
            reason
            value
          }
          updatedAt
          staffsEmployeesAtThirdPartyEndClientSites
          shipOriginalApprovalNotices
          preferredShippingSpeedForDocuments
          placesEmployeesAtClientSitesForMoreThanACoupleOfDaysAtATime
          phone
          permissionReleasePetitionsRFE
          permissionReleaseLCA
          permissionReleaseApprovalNotices
          permissionRelease9089
          permissionPowerAttorney
          paymentPreference
          partTimeEmployeesInUS
          netRevenueForMostRecentFiscalYear {
            amount
            currency
          }
          naicsCodeOfCompany
          maintainsAWrittenImmigrationPolicy
          id
          grossRevenueForMostRecentFiscalYear {
            amount
            currency
          }
          fullTimeEmployeesInUS
          fein
          fax
          employeesEmailMHAttorneysAndParalegalsDirectly
          educationEvaluation
          doingBusinessAs
          createdAt
          companyCoversExpenseOfDependentPetitionsAndOrEADCards
          companyCoversExpenseOfDependentPetitions
          placesEmployeesAtClientSitesForMoreThan30DaysAtATime
          isH1BDependentEmloyer
          hasBeenWillfulViolator
          isHigherEd_20_USC_1001_a
          isHigherEdNonProfit_8_CFR_214_2_h_19_iii_B
          isHigherEdNonProfit_8_CFR_214_2_h_8_ii_F_2
          isNonProfitResearch_8_CFR_214_2_h_19_iii_C
          isNonProfitResearch_8_CFR_214_2_h_8_ii_F_3
          isQualifyingCapExempt_8_CFR_214_2_h_8_ii_F_4
          technologyReleaseLicenseRequirement
          employs50OrMore {
            value
            followPrompt
            followValue
          }
          allowsEmployeesToTelecommute {
            value
            followPrompt
            followValue
            followPromptSecond
            followValueSecond
          }
          preferenceForEmployeesToPayPartOfGreenCard {
            value
            reason
          }
          preferenceForEmployeesToPayPartOfH1B {
            value
            reason
          }
        }
      }
      sponsoringNational {
        id
        firstname
        lastname
        slug
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
      }
      foreignNational {
        type
        id
        slug
        email
        emailPersonal
        emailWork
        employeeId
        username
        firstname
        middlename
        lastname
        nickname
        additionalFirstnames
        additionalMiddlenames
        additionalLastnames
        lowerFirst
        lowerLast
        lowerFull
        maritalStatus
        spouse
        dependents
        phone
        phoneWork
        companyID
        mostRecentlyInvited
        archived
        relatives
        relativeAdmins
        nonImmigrantVisaNumber
        visaIssueDate
        visaExpiration
        visaConsulate
        immigrantVisaApplicant
        immigrantPetitionEverFiled
        hasPendingETA9089
        hasValidOrExpiredEAD
        hasPendingUSCISApplication
        hasHadVisaPetitionRevokedOrDenied
        hasBeenArrestedSinceLastUSEntry
        hasTransportedWeapons
        hasParticipatedInArmedGroup
        hasDetailedPerson
        hasUsedWeapons
        hasWeaponsTraining
        hasCriminalOffence
        hasViolatedVisaTerms
        citizenshipCountry
        birthCountry
        birthState
        birthCity
        birthDate
        jobDescriptionID
        isRemote
        hasNonUSPassport
        passportGender
        passportIssueDate
        passportExpirationDate
        passportNumber
        passportCountry
        usSSN
        aNumber
        lastEntryUS
        i94ArrivalDepartureRecordNumber
        sevisNumber
        eadNumber
        createdAt
        updatedAt
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
        immigrationStatus {
          status
          expiration
        }
        cases {
          items {
            id
            slug
            state
            startDate
            requestedProcess
            requestedProcessSubCategory
            requestedProcessAdditionalProcessing
            globalRequestedProcessSubCategory
            globalProcessCountryCode
            source {
              requestedProcess
            }
          }
        }
        relativesAsSource {
          items {
            id
            type
            target {
              firstname
              id
              lastname
              slug
              birthDate
              immigrationStatus {
                status
                expiration
              }
              avatar {
                original {
                  bucket
                  identityId
                  key
                  level
                  region
                }
                squareThumbnails {
                  size
                  image {
                    bucket
                    identityId
                    key
                    level
                    region
                  }
                }
              }
              cases {
                items {
                  id
                  slug
                  state
                  startDate
                  requestedProcess
                  requestedProcessSubCategory
                  requestedProcessAdditionalProcessing
                  globalRequestedProcessSubCategory
                  globalProcessCountryCode
                  source {
                    requestedProcess
                  }
                }
              }
            }
          }
        }
        relativesAsTarget {
          items {
            id
            type
            source {
              firstname
              id
              lastname
              slug
              birthDate
              immigrationStatus {
                status
                expiration
              }
              avatar {
                original {
                  bucket
                  identityId
                  key
                  level
                  region
                }
                squareThumbnails {
                  size
                  image {
                    bucket
                    identityId
                    key
                    level
                    region
                  }
                }
              }
              cases {
                items {
                  id
                  slug
                  state
                  startDate
                  requestedProcess
                  requestedProcessSubCategory
                  requestedProcessAdditionalProcessing
                  globalRequestedProcessSubCategory
                  globalProcessCountryCode
                  source {
                    requestedProcess
                  }
                }
              }
            }
          }
        }
      }
      sponsoringEmployer {
        id
        firstname
        lastname
        slug
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
      }
      attorney {
        id
        firstname
        lastname
        slug
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
      }
      caseManager {
        id
        firstname
        lastname
        email
        slug
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
      }
      caseNotes(
        userGroupPostDate: { beginsWith: { userGroup: $messagesUserGroup } }
      ) {
        items {
          authorEmployer {
            id
            lastname
            firstname
            avatar {
              original {
                bucket
                identityId
                key
                level
                region
              }
              squareThumbnails {
                image {
                  region
                  level
                  key
                  identityId
                  bucket
                }
                size
              }
            }
          }
          authorTeamMember {
            id
            lastname
            firstname
            avatar {
              original {
                bucket
                identityId
                key
                level
                region
              }
              squareThumbnails {
                image {
                  region
                  level
                  key
                  identityId
                  bucket
                }
                size
              }
            }
          }
          id
          createdAt
          note
        }
      }
      contributors {
        items {
          id
          contributorID
          contributorEmployer {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          contributorForeignNational {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          contributorTeamMember {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
        }
      }
      tasks {
        items {
          action
          category
          deadline
          deadlineIsManuallySet
          description
          duration
          groupsCanRead
          isCustom
          isKeyDate
          keyDateAlternateAction
          isPaymentStatus
          paymentStatusAlternateAction
          orderIndex
          assignedToID
          orderIndex
          isAssignedToLegalAssistants
          assignedToTeamMember {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          assignedToForeignNational {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          assignedToEmployer {
            id
            type
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          caseID
          case {
            attorneyID
            caseManagerID
          }
          caseState
          dependencies {
            items {
              id
              state
              deadline
              duration
              templateID
              startDate
              prerequisiteID
              action
              completeDate
            }
          }
          state
          startDate
          id
          prerequisiteID
          prerequisite {
            action
            category
          }
        }
      }
      ### End fields ###
    }
  }
`;

export const searchCases = /* GraphQL */ `
  query CustomSearchCases(
    $filter: SearchableCaseFilterInput
    $sort: [SearchableCaseSortInput]
    $limit: Int
    $nextToken: String
    $from: Int
  ) {
    searchCases(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
      from: $from
    ) {
      items {
        id
        slug
        caseNumber
        requestedProcess
        sponsoringEmployerID
        sponsoringNationalID
        lowerCompanyName
        lowerNationalName
        lowerProcessType
        nationalVisaExpiration
        currentPhase
        caseManager {
          id
          slug
          firstname
          lastname
        }
        company {
          id
          name
          displayCompanyName
          slug
        }
        foreignNationalID
        foreignNational {
          id
          firstname
          lastname
          slug
          immigrationStatus {
            expiration
          }
        }
        phaseRequirements {
          phase
          milestones
        }
        completedMilestones {
          completionDate
          completedByID
          milestone
          receiptNumber
        }
        startDate
        state
      }
      nextToken
      total
    }
  }
`;

export const onCreateCaseFile = /* GraphQL */ `
  subscription CustomOnCreateCaseFile($caseID: ID!) {
    onCreateCaseFile(caseID: $caseID) {
      id
      fileID
      caseID
      createdAt
      updatedAt
      case {
        id
      }
      file {
        __typename
        id
        expirationDate
        fileName
        fileType
        contentType
        fileRequirement
        fileCategory
        isFlattened
        original {
          bucket
          region
          key
          level
          identityId
        }
        processRequestID
        companyID
        sourceID
        ownerNationalID
        uploadDate
        uploaderID
        uploaderEmployer {
          id
          firstname
          lastname
          lowerFull
          slug
          type
          avatar {
            original {
              bucket
              key
              level
              region
              identityId
            }
            squareThumbnails {
              image {
                bucket
                key
                level
                region
                identityId
              }
              size
            }
            version
          }
        }
        uploaderForeignNational {
          id
          firstname
          lastname
          lowerFull
          slug
          type
          avatar {
            original {
              bucket
              key
              level
              region
              identityId
            }
            squareThumbnails {
              image {
                bucket
                key
                level
                region
                identityId
              }
              size
            }
            version
          }
        }
        uploaderTeamMember {
          id
          firstname
          lastname
          lowerFull
          slug
          type
          avatar {
            original {
              bucket
              key
              level
              region
              identityId
            }
            squareThumbnails {
              image {
                bucket
                key
                level
                region
                identityId
              }
              size
            }
            version
          }
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const caseCompanyData = /* GraphQL */ `
  query CaseCompanyData($id: ID!) {
    getCase(id: $id) {
      requestedProcess
      company {
        id
        files {
          items {
            id
            fileType
            ownerNationalID
            uploaderID
            uploaderTeamMemberID
            uploaderForeignNationalID
            uploaderEmployerID
          }
        }
        fileRequirements {
          type
          assignedToID
          isAssignedToLegalAssistants
          expirationDateRequired
          durationOfStatus
          issueDateRequired
          isApproved
          isRequested
          hidden
          durationOfStatus
        }
      }
      foreignNationalID
      foreignNational {
        type
        id
        files {
          items {
            id
            fileType
            uploaderID
            uploaderTeamMemberID
            uploaderForeignNationalID
            uploaderEmployerID
          }
        }
        fileRequirements {
          type
          assignedToID
          isAssignedToLegalAssistants
          expirationDateRequired
          durationOfStatus
          issueDateRequired
          isApproved
          isRequested
          hidden
          durationOfStatus
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const CustomListCases = /* GraphQL */ `
  query CustomListCases(
    $filter: ModelCaseFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listCases(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;
